import { Observable } from './event.js';

/**
 * Base class for any Disposable class
 * @public
 */ class Disposable extends Observable {
    _disposed;
    constructor(){
        super();
        this._disposed = false;
    }
    get disposed() {
        return this._disposed;
    }
    dispose() {
        if (!this._disposed) {
            this.dispatchEvent('dispose');
            this.onDispose();
            this._disposed = true;
        }
    }
    onDispose() {}
}
/**
 * Maps disposable objects to their reference counts.
 * @internal
 */ const objectReferenceMap = new WeakMap();
/**
 * Maps disposable objects to their weak references.
 * @internal
 */ const weakRefMap = new WeakMap();
/**
 * Holds objects that are pending disposal.
 * @internal
 */ const disposalQueue = new Set();
/**
 * Processes all pending disposals from the previous frame.
 * This should be called at the beginning of each frame
 * @public
 */ function flushPendingDisposals() {
    for (const obj of disposalQueue){
        obj.dispose();
        weakRefMap.get(obj)?.forEach((weakRef)=>{
            weakRef.dispose();
        });
    }
    disposalQueue.clear();
}
/**
 * Retains a disposable object
 * @param obj - Object to retain
 * @public
 *
 * @remarks
 * Retains an object will increase the reference counter for this object
 */ function retainObject(obj) {
    if (obj) {
        const ref = objectReferenceMap.get(obj) ?? 0;
        objectReferenceMap.set(obj, ref + 1);
        if (ref === 0) {
            disposalQueue.delete(obj);
        }
    }
}
/**
 * Releases a disposable object
 * @param obj - Object to release
 * @public
 *
 * @remarks
 * Releases an object will decrease the reference counter for this object.
 * If reference counter become zero, the object will be disposed at next frame.
 */ function releaseObject(obj) {
    if (obj) {
        let refcount = objectReferenceMap.get(obj) ?? 0;
        if (refcount > 0) {
            refcount--;
            if (refcount > 0) {
                objectReferenceMap.set(obj, refcount);
            } else {
                objectReferenceMap.delete(obj);
                disposalQueue.add(obj);
            }
        }
    }
}
/**
 * A reference-counting wrapper for disposable objects.
 *
 * @public
 */ class DRef {
    /** @internal */ _object;
    /**
   * Creates a new reference to a disposable object.
   * @param obj - The disposable object to reference
   */ constructor(obj){
        this._object = obj ?? null;
        retainObject(this._object);
    }
    /**
   * Gets the currently referenced object.
   * @returns The referenced object, or null if none is set
   */ get() {
        return this._object;
    }
    /**
   * Sets a new object reference, releasing the previous one if it exists.
   * @param obj - The new object to reference
   */ set(obj) {
        if (obj !== this._object) {
            releaseObject(this._object);
            this._object = obj;
            retainObject(this._object);
        }
    }
    /**
   * Releases the reference and cleans up resources.
   */ dispose() {
        releaseObject(this._object);
        this._object = null;
    }
}
/**
 * A weak-reference-counting wrapper for disposable objects.
 *
 * @public
 */ class DWeakRef {
    /** @internal */ _object;
    /**
   * Creates a new reference to a disposable object.
   * @param obj - The disposable object to reference
   */ constructor(obj){
        this._object = obj ?? null;
        this.retain();
    }
    /**
   * Gets the currently referenced object.
   * @returns The referenced object, or null if none is set
   */ get() {
        if (this._object?.disposed) {
            this.dispose();
        }
        return this._object;
    }
    /**
   * Sets a new object reference, releasing the previous one if it exists.
   * @param obj - The new object to reference
   */ set(obj) {
        if (obj !== this._object) {
            this.release();
            this._object = obj;
            this.retain();
        }
    }
    /**
   * Releases the reference and cleans up resources.
   */ dispose() {
        this.release();
    }
    /** @internal */ retain() {
        if (this._object) {
            const weakRefList = weakRefMap.get(this._object);
            if (weakRefList) {
                weakRefList.add(this);
            } else {
                weakRefMap.set(this._object, new Set([
                    this
                ]));
            }
        }
    }
    /** @internal */ release() {
        if (this._object) {
            weakRefMap.get(this._object)?.delete(this);
            this._object = null;
        }
    }
}

export { DRef, DWeakRef, Disposable, flushPendingDisposals, releaseObject, retainObject };
//# sourceMappingURL=ref.js.map
